'use client';

/**
 * VerifyXField — single-button "Verify X" UI for board metadata forms.
 *
 * Renders one of two states:
 *   - Empty:    a "Verify X" button. Clicking opens the OAuth popup;
 *               on success the field fills with the verified handle.
 *   - Verified: a `@handle ✓` pill with a small "Verify a different X"
 *               link and a clear button.
 *
 * The verified handle is OAuth-proven via `/api/auth/twitter/start`
 * → X → `/api/auth/twitter/callback`, and the proof is persisted in
 * `verified_x_handles`. The board metadata writer enforces the proof
 * at save time, so this component is purely presentational gating —
 * the server is the security boundary.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, X as XIcon } from 'lucide-react';
import Image from 'next/image';

import { cn } from '@/lib/utils';
import {
  consumeVerifyXReturnFromUrl,
  useVerifyX,
} from '@/hooks/use-verify-x';

interface VerifiedHandle {
  username: string;
  avatarUrl: string | null;
}

export interface VerifyXFieldProps {
  /** Current handle (without `@`). Empty string = not connected. */
  value: string;
  /** Setter — called with the new handle (or empty string to clear). */
  onChange: (next: string) => void;
  /** Optional: show a smaller variant for tight rows (edit panel). */
  size?: 'md' | 'sm';
  /** Optional: disable while the parent form is mid-save. */
  disabled?: boolean;
  /** Optional className applied to the wrapper. */
  className?: string;
}

export function VerifyXField({
  value,
  onChange,
  size = 'md',
  disabled,
  className,
}: VerifyXFieldProps) {
  const { start, pending } = useVerifyX();
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState<VerifiedHandle[]>([]);

  // Hydrate the user's existing verified handles so we can show an
  // avatar in the pill without making the user re-OAuth on every page
  // load. Failures are silent — the field still works without an
  // avatar; we just won't decorate it.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/twitter/handles', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled || !j?.data) return;
        setVerified(j.data as VerifiedHandle[]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Return-from-redirect handling.
  //
  // In the mobile/in-app webview transport (`useVerifyX` redirect
  // mode) the OAuth callback 302s the tab back to the page that
  // started the flow with `?x_verified=<handle>` (or
  // `?x_verify_error=…`). Apply the result to the parent's state on
  // first mount, then strip the params from the URL so a refresh
  // doesn't replay them.
  //
  // We use the latest `onChange` via a ref so the effect itself only
  // runs once — re-running on every render would loop (the parent
  // patches state → re-render → same params still present until the
  // history is replaced, but we'd be racing the replace).
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const consumed = consumeVerifyXReturnFromUrl(window.location.href);
    if (!consumed.consumed) return;
    if (consumed.username) {
      onChangeRef.current(consumed.username);
      // Optimistically merge into the local verified list so the pill
      // shows a green check + avatar immediately; the parallel
      // `/handles` fetch above will fill in the avatar URL once it
      // resolves.
      setVerified((prev) => {
        const lower = consumed.username!.toLowerCase();
        const others = prev.filter(
          (v) => v.username.toLowerCase() !== lower,
        );
        return [
          { username: consumed.username!, avatarUrl: null },
          ...others,
        ];
      });
    }
    if (consumed.error) {
      setError(consumed.error);
    }
    // Replace history so refresh / back doesn't re-trigger this branch.
    try {
      window.history.replaceState(
        window.history.state,
        '',
        consumed.cleanUrl,
      );
    } catch {
      // Some sandboxed contexts disallow replaceState — non-fatal,
      // the params just stay in the URL.
    }
  }, []);

  const trimmed = value.trim().replace(/^@/, '');
  const matchedAvatar = useMemo(() => {
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    const hit = verified.find((v) => v.username.toLowerCase() === lower);
    return hit?.avatarUrl ?? null;
  }, [trimmed, verified]);

  // Whether the *current* value has a matching proof. We don't display
  // "verified" decoration unless we can confirm it from the server's
  // verified-handles list — for legacy URL-only rows that get parsed
  // into a handle on load, the user must re-OAuth to get the green
  // checkmark (and to satisfy the server's save-time gate).
  const isProven = useMemo(() => {
    if (!trimmed) return false;
    const lower = trimmed.toLowerCase();
    return verified.some((v) => v.username.toLowerCase() === lower);
  }, [trimmed, verified]);

  const onVerify = useCallback(async () => {
    setError(null);
    try {
      const result = await start();
      onChange(result.username);
      // Optimistically merge into the local verified list so the pill
      // immediately shows the green check + avatar, without waiting
      // for the next `/handles` fetch.
      setVerified((prev) => {
        const others = prev.filter(
          (v) => v.username.toLowerCase() !== result.username.toLowerCase(),
        );
        return [
          { username: result.username, avatarUrl: result.avatarUrl ?? null },
          ...others,
        ];
      });
    } catch (err) {
      setError((err as Error).message);
    }
  }, [start, onChange]);

  const onClear = useCallback(() => {
    setError(null);
    onChange('');
  }, [onChange]);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {trimmed ? (
        <VerifiedPill
          handle={trimmed}
          avatarUrl={matchedAvatar}
          isProven={isProven}
          onReverify={onVerify}
          onClear={onClear}
          pending={pending}
          disabled={disabled}
          size={size}
        />
      ) : (
        <VerifyButton
          onClick={onVerify}
          pending={pending}
          disabled={disabled}
          size={size}
        />
      )}
      {error && (
        <p
          role="alert"
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-red-300/85"
        >
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function VerifyButton({
  onClick,
  pending,
  disabled,
  size,
}: {
  onClick: () => void;
  pending: boolean;
  disabled?: boolean;
  size: 'md' | 'sm';
}) {
  const padding =
    size === 'sm' ? 'px-2.5 py-1 text-[11px]' : 'px-3.5 py-1.5 text-[12px]';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || pending}
      className={cn(
        'inline-flex w-fit items-center gap-2 rounded-[5px] border font-mono uppercase tracking-[0.15em] transition-colors',
        padding,
        'border-amber-400/45 bg-gradient-to-b from-[#1c150a] via-[#0c0a06] to-[#1c150a] text-amber-100 hover:border-amber-300/80',
        'disabled:opacity-55 disabled:cursor-not-allowed',
      )}
    >
      <XLogo className="h-3 w-3" />
      <span>{pending ? 'Verifying…' : 'Verify X'}</span>
      {pending && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
    </button>
  );
}

function VerifiedPill({
  handle,
  avatarUrl,
  isProven,
  onReverify,
  onClear,
  pending,
  disabled,
  size,
}: {
  handle: string;
  avatarUrl: string | null;
  isProven: boolean;
  onReverify: () => void;
  onClear: () => void;
  pending: boolean;
  disabled?: boolean;
  size: 'md' | 'sm';
}) {
  const dim = size === 'sm';
  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          'inline-flex items-center gap-2 rounded-full border bg-black/45',
          dim ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-[12px]',
          isProven
            ? 'border-emerald-500/45 text-emerald-100'
            : 'border-amber-300/40 text-amber-100/85',
        )}
        title={
          isProven
            ? 'Ownership verified via X OAuth'
            : 'Legacy handle — re-verify to keep using it'
        }
      >
        {avatarUrl ? (
          <Image
            src={avatarUrl}
            alt=""
            width={18}
            height={18}
            className="h-[18px] w-[18px] rounded-full"
            // Twitter CDN images don't need our optimization pipeline
            // and would otherwise count against the free Vercel quota.
            unoptimized
          />
        ) : (
          <XLogo className="h-3 w-3" />
        )}
        <span className="font-mono">@{handle}</span>
        {isProven ? (
          <Check
            className="h-3 w-3 text-emerald-300"
            aria-label="Verified"
          />
        ) : (
          <span
            className="font-mono text-[9px] uppercase tracking-[0.18em] text-amber-300/80"
            aria-label="Unverified"
          >
            !
          </span>
        )}
        <button
          type="button"
          onClick={onClear}
          disabled={disabled || pending}
          className="ml-0.5 grid h-4 w-4 place-items-center rounded-full text-current/60 transition-colors hover:text-current disabled:opacity-40"
          aria-label="Remove X handle"
        >
          <XIcon className="h-3 w-3" />
        </button>
      </div>
      <button
        type="button"
        onClick={onReverify}
        disabled={disabled || pending}
        className={cn(
          'font-mono uppercase tracking-[0.18em] underline-offset-2 hover:underline',
          dim ? 'text-[9px]' : 'text-[10px]',
          isProven ? 'text-amber-100/55' : 'text-amber-300/85',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        {pending
          ? 'Verifying…'
          : isProven
            ? 'Verify a different X'
            : 'Verify X'}
      </button>
    </div>
  );
}

/**
 * Inline X logo SVG. We embed it instead of pulling lucide's `Twitter`
 * icon because Lucide's mark is the legacy bird; X's current brand is
 * the rotated cross. Kept tiny so the bundle hit is negligible.
 */
function XLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      className={className}
      fill="currentColor"
    >
      <path d="M18.244 2H21l-6.55 7.49L22 22h-6.78l-4.92-6.43L4.6 22H2l7.04-8.05L1.5 2h6.94l4.45 5.88L18.244 2zm-2.38 18h1.88L8.22 4H6.22l9.644 16z" />
    </svg>
  );
}
